import React, { useEffect, useState } from 'react';
import Api from 'api';
import { openExternalUrl } from 'utils/nativeApp';
import { useCopyToClipboard } from 'hooks/useCopyToClipboard';
import {
  Card,
  Title,
  Subtitle,
  CodeRow,
  CodeValue,
  CopyButton,
  ShareRow,
  ShareLabel,
  ShareLink,
  CreditsBadge,
  Explainer,
  ErrorText,
  ActivityCounts,
} from './ReferralCodeCard.styles';

interface ReferralCodeCardProps {
  /** 'talent' shows a gift-card explainer; 'office' shows the free-placement-credit balance. */
  audience: 'talent' | 'office';
  /** Talent country for the gift-card explainer: 0 = Canada ($50 CAD), 1 = USA ($25 USD). */
  country?: 0 | 1;
}

interface ReferralData {
  referralCode: string;
  shareUrl: string;
  freePlacementCredits: number;
  // Optional during an API-first rollout; missing counts are not zero activity.
  referredCount?: number;
  completedReferralCount?: number;
}

/**
 * Shared referral code card. Used on talent Profile/MyStats and the office Locations
 * page. Self-contained: fetches the user's code/share link/credit balance and provides
 * copy-to-clipboard. Office variant also shows the Free Placement Credits balance.
 */
export default function ReferralCodeCard({ audience, country }: ReferralCodeCardProps) {
  const [data, setData] = useState<ReferralData | null>(null);
  const [error, setError] = useState<string>('');
  const [copied, setCopied] = useState<'' | 'code' | 'link'>('');
  const { copyToClipboard } = useCopyToClipboard();
  // T2-5090: bumping this re-runs the fetch — a transient failure (API restart, network blip)
  // previously dead-ended the card on the error message with no way to recover except a full
  // page reload.
  const [fetchAttempt, setFetchAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    setError('');
    Api.getReferralCode()
      .then((res) => {
        if (active) setData(res);
      })
      .catch(() => {
        if (active) setError('Could not load your referral code.');
      });
    return () => {
      active = false;
    };
  }, [fetchAttempt]);

  const retry = () => setFetchAttempt((n) => n + 1);

  const copy = async (value: string, which: 'code' | 'link') => {
    if (await copyToClipboard(value)) {
      setCopied(which);
      setTimeout(() => setCopied(''), 2000);
    }
  };

  const openShareLink = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    const refCode = (() => {
      try {
        const url = new URL(data?.shareUrl || '', window.location.href);
        return url.searchParams.get('ref') || url.searchParams.get('refCode') || data?.referralCode || '';
      } catch {
        return data?.referralCode || '';
      }
    })();

    const goReact = (window as any).TempStars?.App?.goReact;
    if (goReact) {
      goReact('landing', { refCode });
      return;
    }

    if (data?.shareUrl) {
      openExternalUrl(data.shareUrl);
    }
  };

  if (error) {
    return (
      <Card>
        <Title>Refer & Earn</Title>
        <ErrorText>{error}</ErrorText>
        <CopyButton type="button" onClick={retry} data-testid="referral-retry">
          Try Again
        </CopyButton>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card>
        <Title>Refer & Earn</Title>
        <Subtitle>Loading your referral code…</Subtitle>
      </Card>
    );
  }

  const explainer =
    audience === 'office'
      ? 'Earn a free placement credit when someone you refer completes their first shift. Credits apply automatically to your next shift.'
      : country === 1
      ? 'Earn a $25 USD Amazon gift card when someone you refer completes their first shift.'
      : 'Earn a $50 CAD Amazon gift card when someone you refer completes their first shift.';

  const hasActivityCounts = Number.isSafeInteger(data.referredCount)
    && Number.isSafeInteger(data.completedReferralCount)
    && data.referredCount! >= 0
    && data.completedReferralCount! >= 0
    && data.completedReferralCount! <= data.referredCount!;

  return (
    <Card>
      <Title>Refer &amp; Earn</Title>
      <Explainer>{explainer}</Explainer>

      <CodeRow>
        <div>
          <Subtitle>Your referral code</Subtitle>
          <CodeValue data-testid="referral-code">{data.referralCode}</CodeValue>
        </div>
        <CopyButton
          type="button"
          data-testid="referral-copy-code-button"
          onClick={() => copy(data.referralCode, 'code')}
        >
          {copied === 'code' ? 'Copied!' : 'Copy'}
        </CopyButton>
      </CodeRow>

      <ShareRow>
        <div>
          <ShareLabel>Share link</ShareLabel>
          <ShareLink
            href={data.shareUrl}
            className="external no-fastclick"
            target="_system"
            onClick={openShareLink}
            aria-label="Open referral sign up link"
            data-testid="referral-share-link"
            content-desc="referral-share-link"
          >
            {data.shareUrl}
          </ShareLink>
        </div>
        <CopyButton
          type="button"
          data-testid="referral-copy-link-button"
          onClick={() => copy(data.shareUrl, 'link')}
        >
          {copied === 'link' ? 'Copied!' : 'Copy'}
        </CopyButton>
      </ShareRow>

      {audience === 'talent' && (hasActivityCounts ? (
        <ActivityCounts aria-label="Referral activity" data-testid="talent-referral-counts">
          <div>
            <dt>People referred</dt>
            <dd data-testid="referral-referred-count">{data.referredCount}</dd>
          </div>
          <div>
            <dt>Completed a shift</dt>
            <dd data-testid="referral-completed-count">{data.completedReferralCount}</dd>
          </div>
        </ActivityCounts>
      ) : (
        <div>
          <ErrorText role="status">Referral counts are currently unavailable.</ErrorText>
          <CopyButton type="button" onClick={retry} data-testid="referral-counts-retry">
            Try Again
          </CopyButton>
        </div>
      ))}

      {audience === 'office' && (
        <CreditsBadge data-testid="free-placement-credits">
          Free Placement Credits: <strong>{data.freePlacementCredits}</strong>
        </CreditsBadge>
      )}
    </Card>
  );
}
